import { Injectable, inject } from '@angular/core';

import { DeviceApplicationStateService } from '@era/appmanagement-sdk';
import {
  AppleDevice,
  DeviceActionsService,
  DeviceOperationsService,
  DevicesService,
  IntegrationMDMDevicesService,
  IntegrationMDMProxyService,
  Device as LocationsDevice,
  SyncService,
} from '@era/locations-sdk';
import { DeviceService as OsUpdatesDeviceService } from '@era/osupdates-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DevicesService as MdmDevicesService } from '@rocksoft/mdm-sdk-v2';
import { map, mergeMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { safeMap } from '../effects.helpers';
import { DevicesRouterStoreSelectors, LocationsRouterStoreSelectors } from '../router';
import {
  BrandViewActions,
  CategorySettingsWallpaperViewActions,
  DeviceActionsActions,
  DeviceApiActions,
  DeviceApiWallpaperActions,
  DeviceAssignViewActions,
  DeviceBarViewActions,
  DeviceDetailsContextMenuActions,
  DeviceFrontDeskViewActions,
  DeviceHousekeepingViewActions,
  DeviceKioskViewActions,
  DeviceModalActions,
  DeviceResolverActions,
  DeviceStaffViewActions,
  DeviceSyncStatusViewActions,
  DeviceUnassignViewActions,
  DevicesSyncModalActions,
  GatewayApiActions,
  LocationViewActions,
} from './device.actions';

import { ExportService } from './export.service';

import { Device } from '@rocksoft/shared';
import { getDevicesFilteredOnSubset } from './utils/device.utils';

@Injectable()
export class DeviceEffects {
  private readonly deviceService = inject(DevicesService);
  private readonly exportService = inject(ExportService);
  private readonly integrationMDMProxyService = inject(IntegrationMDMProxyService);
  private readonly appleDeviceService = inject(IntegrationMDMDevicesService);
  private readonly deviceApplicationStateService = inject(DeviceApplicationStateService);
  private readonly mdmDevicesService = inject(MdmDevicesService);
  private readonly actions$ = inject(Actions);
  private readonly deviceActionsService = inject(DeviceActionsService);
  private readonly deviceOperationsService = inject(DeviceOperationsService);
  private readonly osUpdatesDeviceService = inject(OsUpdatesDeviceService);
  private readonly devicesSyncService = inject(SyncService);
  private readonly store = inject(Store);

  // ============================================================================
  // RESOLVER EFFECTS - Basic Device Info
  // ============================================================================

  devicesLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceResolverActions.devicesRequested),
      mergeMap(({ brandId, locationId }) =>
        safeMap(
          this.deviceService.getDevices({ brandUniqueId: brandId, locationUniqueId: locationId }),
          (devices: LocationsDevice[]) => DeviceApiActions.basicDeviceInfoLoaded({ devices }),
          (error) => DeviceApiActions.basicDeviceInfoLoadError({ error })
        )
      )
    );
  });

  devicesForStateLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceResolverActions.devicesForStateRequested),
      mergeMap(({ state }) =>
        safeMap(
          this.deviceService.getDevicesByDeviceState({ state }),
          (devices: LocationsDevice[]) => DeviceApiActions.basicDeviceInfoLoaded({ devices }),
          (error) => DeviceApiActions.basicDeviceInfoLoadError({ error })
        )
      )
    );
  });

  // ============================================================================
  // RESOLVER EFFECTS - Apple Device Info
  // ============================================================================

  appleDeviceLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceResolverActions.appleDeviceRequested),
      mergeMap(({ udid }) =>
        safeMap(
          this.appleDeviceService.getAppleDevice({ udid }),
          (device: AppleDevice) => DeviceApiActions.appleDeviceInfoLoaded({ appleDevice: { ...device, udid } }),
          (error) => DeviceApiActions.appleDeviceInfoLoadError({ error })
        )
      )
    );
  });

  // ============================================================================
  // RESOLVER EFFECTS - Application State
  // ============================================================================

  deviceApplicationStatesLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceResolverActions.deviceApplicationStatesRequested),
      mergeMap(() =>
        safeMap(
          this.deviceApplicationStateService.getAllDeviceStates(),
          (states) => DeviceApiActions.applicationStateLoaded({ states }),
          (error) => DeviceApiActions.applicationStateLoadError({ error })
        )
      )
    );
  });

  // ============================================================================
  // RESOLVER EFFECTS - MDM Devices
  // ============================================================================

  mdmDevicesLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceResolverActions.mdmDevicesRequested),
      mergeMap(() =>
        safeMap(
          this.mdmDevicesService.getDevices({ apiVersion: '2.0' }),
          (devices) => DeviceApiActions.mdmDeviceInfoLoaded({ devices }),
          (error) => DeviceApiActions.mdmDeviceInfoLoadError({ error })
        )
      )
    );
  });

  mdmDeviceLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceResolverActions.mdmDeviceRequested),
      mergeMap(({ udid }) =>
        safeMap(
          this.mdmDevicesService.getDevice({ deviceType: 'Apple', id: udid, apiVersion: '2.0' }),
          (device) => DeviceApiActions.mdmDeviceInfoLoaded({ devices: [device] }),
          (error) => DeviceApiActions.mdmDeviceInfoLoadError({ error })
        )
      )
    );
  });

  // ============================================================================
  // DEVICE ASSIGNMENT EFFECTS
  // ============================================================================

  deviceUnassignedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceUnassignViewActions.deviceUnassigned),
      mergeMap(({ deviceLink }) =>
        safeMap(
          this.deviceActionsService.unassignDevice({ brandId: 'default-brand', deviceLink }),
          (device) => {
            // Optional properties are not removed, so we have to set them manually.
            device.locationUniqueId = null;
            device.roomUniqueId = null;
            device.housekeepingUniqueId = null;
            device.frontDeskUniqueId = null;
            device.barUniqueId = null;
            device.staffUniqueId = null;
            device.kioskUniqueId = null;
            return DeviceApiActions.deviceUnassigned({ device });
          },
          (error) => DeviceApiActions.deviceUnassignedError({ error })
        )
      )
    );
  });

  deviceMarkedAsUnAvailableEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceUnassignViewActions.deviceUnavailable),
      mergeMap(({ deviceLink }) =>
        safeMap(
          this.deviceActionsService.markDeviceAsUnavailable({ deviceLink }),
          (device) => {
            device.locationUniqueId = null;
            device.roomUniqueId = null;
            device.housekeepingUniqueId = null;
            device.frontDeskUniqueId = null;
            device.barUniqueId = null;
            device.staffUniqueId = null;
            device.kioskUniqueId = null;
            return DeviceApiActions.deviceUnavailable({ device });
          },
          (error) => DeviceApiActions.deviceUnavailableError({ error })
        )
      )
    );
  });

  deviceAssignedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceAssignViewActions.deviceAssigned),
      mergeMap(({ deviceLink, locationId, roomId, brandId }) =>
        safeMap(
          this.deviceActionsService.assignDevice({ locationId, roomId, brandId, deviceLink }),
          (device) => {
            device.housekeepingUniqueId = null;
            device.staffUniqueId = null;
            device.frontDeskUniqueId = null;
            device.barUniqueId = null;
            device.kioskUniqueId = null;
            return DeviceApiActions.deviceAssigned({ device });
          },
          (error) => DeviceApiActions.deviceAssignedError({ error })
        )
      )
    );
  });

  deviceMarkedAsSpareEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceAssignViewActions.deviceMarkedAsSpare),
      mergeMap(({ deviceLink, locationId, brandId }) =>
        safeMap(
          this.deviceActionsService.markDeviceAsSpare({ locationId, brandId, deviceLink }),
          (device) => {
            device.roomUniqueId = null;
            device.housekeepingUniqueId = null;
            device.staffUniqueId = null;
            device.frontDeskUniqueId = null;
            device.barUniqueId = null;
            device.kioskUniqueId = null;
            return DeviceApiActions.deviceMarkedAsSpare({ device });
          },
          (error) => DeviceApiActions.deviceMarkedAsSpareError({ error })
        )
      )
    );
  });

  deviceAssignToHousekeeping$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceHousekeepingViewActions.deviceMarkedAsHousekeeping),
      mergeMap(({ deviceLink, locationId, brandId }) =>
        safeMap(
          this.deviceActionsService.assignHousekeepingDevice({ locationId, brandId, deviceLink }),
          (device) => {
            device.roomUniqueId = null;
            device.staffUniqueId = null;
            device.frontDeskUniqueId = null;
            device.barUniqueId = null;
            device.kioskUniqueId = null;
            return DeviceApiActions.deviceMarkedAsHousekeeping({ device });
          },
          (error) => DeviceApiActions.deviceMarkedAsHousekeepingError({ error })
        )
      )
    );
  });

  deviceAssignToStaff$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceStaffViewActions.deviceMarkedAsStaff),
      mergeMap(({ deviceLink, locationId, brandId }) =>
        safeMap(
          this.deviceActionsService.assignStaffDevice({ locationId, brandId, deviceLink }),
          (device) => {
            device.roomUniqueId = null;
            device.housekeepingUniqueId = null;
            device.frontDeskUniqueId = null;
            device.barUniqueId = null;
            device.kioskUniqueId = null;
            return DeviceApiActions.deviceMarkedAsStaff({ device });
          },
          (error) => DeviceApiActions.deviceMarkedAsStaffError({ error })
        )
      )
    );
  });

  deviceAssignToFrontDesk$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceFrontDeskViewActions.deviceMarkedAsFrontDesk),
      mergeMap(({ deviceLink, locationId, brandId }) =>
        safeMap(
          this.deviceActionsService.assignFrontDeskDevice({ locationId, brandId, deviceLink }),
          (device) => {
            device.roomUniqueId = null;
            device.housekeepingUniqueId = null;
            device.staffUniqueId = null;
            device.barUniqueId = null;
            device.kioskUniqueId = null;
            return DeviceApiActions.deviceMarkedAsFrontDesk({ device });
          },
          (error) => DeviceApiActions.deviceMarkedAsFrontDeskError({ error })
        )
      )
    );
  });

  deviceAssignToBar$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceBarViewActions.deviceMarkedAsBar),
      mergeMap(({ deviceLink, locationId, brandId }) =>
        safeMap(
          this.deviceActionsService.assignBarDevice({ locationId, brandId, deviceLink }),
          (device) => {
            device.roomUniqueId = null;
            device.housekeepingUniqueId = null;
            device.staffUniqueId = null;
            device.frontDeskUniqueId = null;
            device.kioskUniqueId = null;
            return DeviceApiActions.deviceMarkedAsBar({ device });
          },
          (error) => DeviceApiActions.deviceMarkedAsBarError({ error })
        )
      )
    );
  });

  deviceAssignToKiosk$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceKioskViewActions.deviceMarkedAsKiosk),
      mergeMap(({ deviceLink, locationId, brandId }) =>
        safeMap(
          this.deviceActionsService.assignKioskDevice({ locationId, brandId, deviceLink }),
          (device) => {
            device.roomUniqueId = null;
            device.housekeepingUniqueId = null;
            device.staffUniqueId = null;
            device.frontDeskUniqueId = null;
            device.barUniqueId = null;
            return DeviceApiActions.deviceMarkedAsKiosk({ device });
          },
          (error) => DeviceApiActions.deviceMarkedAsKioskError({ error })
        )
      )
    );
  });

  // ============================================================================
  // SYNC EFFECTS
  // ============================================================================

  deviceSyncRetryEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceSyncStatusViewActions.retrySync),
      mergeMap(({ udid, forceSync }) =>
        safeMap(
          this.deviceActionsService.syncDevice({ deviceId: udid, deviceType: 'Apple', force: forceSync }),
          () => DeviceApiActions.syncRetried({ udid }),
          (error) => DeviceApiActions.deviceSyncRetryError({ error })
        )
      )
    );
  });

  deviceRetryGivenSyncEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceSyncStatusViewActions.retryGivenSync),
      withLatestFrom(this.store.select(DevicesRouterStoreSelectors.selectUdid)),
      mergeMap(([{ syncType, forceSync }, udid]) =>
        safeMap(
          this.deviceActionsService.syncDevice({
            deviceId: udid ?? '',
            deviceType: 'Apple',
            force: forceSync,
            syncTypes: [syncType],
          }),
          () => DeviceApiActions.givenSyncRetried({ udid: udid ?? '', syncType }),
          (error) => DeviceApiActions.deviceGivenSyncRetryError({ error })
        )
      )
    );
  });

  devicesSyncRequestedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DevicesSyncModalActions.syncDevicesRequested),
      mergeMap(({ locationSubset, force, syncTypes }) =>
        getDevicesFilteredOnSubset(this.store, locationSubset).pipe(
          take(1),
          map((devices: Device[]) => devices.map((device) => device.udid)),
          mergeMap((deviceIds: string[]) =>
            safeMap(
              this.devicesSyncService.syncDevices({
                force,
                syncTypes,
                devicesQuery: { selector: { devices: deviceIds } },
              }),
              () => DeviceApiActions.syncDevicesRequestSuccessful({ udids: deviceIds, syncTypes }),
              (error) => DeviceApiActions.deviceGivenSyncRetryError({ error })
            )
          )
        )
      )
    );
  });

  // ============================================================================
  // OS UPDATE EFFECTS
  // ============================================================================

  deviceOsUpdateResetEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceSyncStatusViewActions.retryOsUpdate),
      mergeMap(({ udid }) =>
        safeMap(
          this.osUpdatesDeviceService.resetOsUpdateState({ udid }),
          () => DeviceApiActions.oSUpdateRetried({ udid }),
          (error) => DeviceApiActions.deviceOsUpdateRetryError({ error })
        )
      )
    );
  });

  deviceOsUpdateContinueEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceSyncStatusViewActions.continueOsUpdate),
      mergeMap(({ udid }) =>
        safeMap(
          this.osUpdatesDeviceService.scheduleOsUpdate({ udid }),
          () => DeviceApiActions.oSUpdateContinued({ udid }),
          (error) => DeviceApiActions.deviceOsUpdateContinueError({ error })
        )
      )
    );
  });

  // ============================================================================
  // LOST MODE EFFECTS
  // ============================================================================

  deviceLostModeEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceDetailsContextMenuActions.deviceLostModeCommandSent),
      mergeMap(({ udid, enableLostModeCommand }) =>
        safeMap(
          this.integrationMDMProxyService.queueEnableLostModeCommand({ udid, enableLostModeCommand }),
          (commandProgress) => DeviceApiActions.devicePutInLostMode({ udid: commandProgress.udid ?? '' }),
          (error) => DeviceApiActions.failedToPutDeviceIntoLostMode({ error })
        )
      )
    );
  });

  deviceDisableLostModeEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceDetailsContextMenuActions.deviceDisableLostModeCommandSent),
      mergeMap(({ udid }) =>
        safeMap(
          this.integrationMDMProxyService.queueDisableLostModeCommand({ udid }),
          (commandProgress) => DeviceApiActions.deviceTakenOutOfLostMode({ udid: commandProgress.udid ?? '' }),
          (error) => DeviceApiActions.failedToTakeDeviceOutOfLostMode({ error })
        )
      )
    );
  });

  // ============================================================================
  // RESTART/REBOOT EFFECTS
  // ============================================================================

  deviceRebootEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceDetailsContextMenuActions.deviceRestartCommandSent),
      mergeMap(({ udid }) =>
        safeMap(
          this.integrationMDMProxyService.queueRestartDeviceCommand({ udid }),
          () => DeviceApiActions.deviceRestartSuccessful(),
          (error) => DeviceApiActions.failedToRestartDevice({ error })
        )
      )
    );
  });

  devicesRebootEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceModalActions.rebootDevicesRequested),
      mergeMap(({ locationSubset, modelNames }) =>
        getDevicesFilteredOnSubset(this.store, locationSubset).pipe(
          take(1),
          map((devices: Device[]) =>
            devices.filter((device) =>
              [...modelNames].some((modelName) => [...(device?.apple?.modelName ?? [])].includes(modelName))
            )
          ),
          map((devices: Device[]) => devices.map((device) => device.udid)),
          mergeMap((deviceIds: string[]) =>
            safeMap(
              this.deviceOperationsService.queueRestartDevicesCommand({
                devicesQuery: { selector: { devices: deviceIds } },
              }),
              () => DeviceApiActions.restartDevicesRequestSuccessful(),
              (error) => DeviceApiActions.restartDevicesError({ error })
            )
          )
        )
      )
    );
  });

  // ============================================================================
  // WALLPAPER EFFECTS
  // ============================================================================

  deviceWallpaperEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategorySettingsWallpaperViewActions.wallpaperCommandSent),
      mergeMap(({ wallpaper, categoryType, categoryId }) =>
        safeMap(
          this.integrationMDMProxyService.queueSetWallpaperCommand({
            image: wallpaper.image,
            where: wallpaper.where,
            brandUniqueId: categoryType === 'Brand' ? categoryId : undefined,
            locationUniqueId: categoryType === 'Location' ? categoryId : undefined,
            roomUniqueId: categoryType === 'Room' ? categoryId : undefined,
          }),
          () => DeviceApiWallpaperActions.wallpaperSuccessfullySet(),
          (error) => DeviceApiWallpaperActions.wallpaperFailedToSet({ error })
        )
      )
    );
  });

  // ============================================================================
  // DEVICE NAME EFFECTS
  // ============================================================================

  deviceSetNameEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceActionsActions.setDesiredName),
      mergeMap(({ deviceId, newName }) =>
        safeMap(
          this.deviceActionsService.setDeviceName({
            setDeviceNameCommand: {
              deviceId: deviceId,
              deviceType: 'Apple',
              deviceName: newName,
            },
          }),
          (device) => DeviceApiActions.setDesiredNameSucceeded({ device }),
          (error) => DeviceApiActions.setDesiredNameFailed({ error })
        )
      )
    );
  });

  // ============================================================================
  // EXPORT EFFECTS
  // ============================================================================

  exportLocationDevicesEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationViewActions.exportDevices),
      withLatestFrom(
        this.store.select(LocationsRouterStoreSelectors.selectBrandId),
        this.store.select(LocationsRouterStoreSelectors.selectLocationHumanId)
      ),
      mergeMap(([, brandId, locationId]) =>
        safeMap(
          this.exportService.getDeviceDetails(brandId, locationId ?? undefined, 'body').pipe(
            tap((res: Blob) => {
              const blob = new Blob([res], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              });
              const uri = window.URL.createObjectURL(blob);
              window.open(uri, 'download');
            })
          ),
          () => GatewayApiActions.exportDevicesSuccess(),
          (error) => GatewayApiActions.exportDevicesFailed({ error })
        )
      )
    );
  });

  exportBrandDevicesEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BrandViewActions.exportDevices),
      withLatestFrom(this.store.select(LocationsRouterStoreSelectors.selectBrandId)),
      mergeMap(([, brandId]) =>
        safeMap(
          this.exportService.getDeviceDetails(brandId, undefined, 'body').pipe(
            tap((res: Blob) => {
              const blob = new Blob([res], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              });
              const uri = window.URL.createObjectURL(blob);
              window.open(uri, 'download');
            })
          ),
          () => GatewayApiActions.exportDevicesSuccess(),
          (error) => GatewayApiActions.exportDevicesFailed({ error })
        )
      )
    );
  });
}
